import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AttachMoney as AttachMoneyIcon,
  PointOfSale as PointOfSaleIcon,
  ReceiptLong as ReceiptLongIcon,
  Refresh as RefreshIcon,
  ShoppingCart as ShoppingCartIcon,
  TrendingUp as TrendingUpIcon,
  WarningAmber as WarningIcon,
  AccountBalanceWallet as WalletIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { getDashboard } from '../services/api';
import { toDateInputValue } from '../utils/datetimeLocal';
import { useTheme } from '@mui/material/styles';
import { useEstabelecimentoFiltroApi } from '../context/EstabelecimentoContext';

/// Intervalo do modo ao vivo. O dashboard só busca quando a aba está visível.
const AUTO_REFRESH_MS = 30000;

/// Tempo que o destaque de "entrou venda nova" fica na tela.
const HIGHLIGHT_MS = 8000;

const CHART_COLORS = [
  '#0495F0',
  '#53CD9D',
  '#D2691E',
  '#A457CD',
  '#CD6357',
  '#53AFCD',
  '#CDBE57',
  '#0047AB',
  '#CD57CC',
  '#36BE4D',
];

const currency = (value) =>
  Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

const localDate = toDateInputValue;

const diaCurto = (iso) =>
  new Date(iso).toLocaleDateString('pt-BR', {
    timeZone: 'UTC',
    day: '2-digit',
    month: '2-digit',
  });

const defaultDates = () => {
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth(), 1);
  return { de: localDate(first), ate: localDate(today) };
};

const rangesRapidos = () => {
  const hoje = new Date();
  const diasAtras = (dias) =>
    new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - dias);
  return [
    { label: 'Hoje', de: localDate(hoje), ate: localDate(hoje) },
    { label: '7 dias', de: localDate(diasAtras(6)), ate: localDate(hoje) },
    { label: '30 dias', de: localDate(diasAtras(29)), ate: localDate(hoje) },
    { ...defaultDates(), label: 'Este mês' },
  ];
};

/// Anima a transição entre dois números para o painel não "pular" no refresh.
function useCountUp(value, duration = 600) {
  const [display, setDisplay] = useState(Number(value) || 0);
  const fromRef = useRef(Number(value) || 0);

  useEffect(() => {
    const alvo = Number(value) || 0;
    const inicio = fromRef.current;
    if (inicio === alvo) return undefined;

    let frame;
    const t0 = performance.now();
    const passo = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      const eased = 1 - (1 - p) ** 3;
      const atual = inicio + (alvo - inicio) * eased;
      setDisplay(atual);
      if (p < 1) frame = requestAnimationFrame(passo);
      else fromRef.current = alvo;
    };
    frame = requestAnimationFrame(passo);
    return () => cancelAnimationFrame(frame);
  }, [value, duration]);

  return display;
}

function KpiCard({
  title,
  value,
  format,
  subtitle,
  icon,
  color = 'primary.main',
  delta,
  onClick,
  hint,
  oculto = false,
}) {
  const animado = useCountUp(oculto ? 0 : value);
  const texto = oculto
    ? '••••'
    : (format ? format(animado) : Math.round(animado).toLocaleString('pt-BR'));
  const clicavel = typeof onClick === 'function';

  const card = (
    <Card
      role={clicavel ? 'button' : undefined}
      tabIndex={clicavel ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        clicavel
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      sx={{
        height: '100%',
        transition: 'transform .2s, box-shadow .2s, border-color .2s',
        cursor: clicavel ? 'pointer' : 'default',
        border: '1px solid',
        borderColor: 'transparent',
        outline: 'none',
        '&:hover': clicavel
          ? {
              transform: 'translateY(-2px)',
              boxShadow: 6,
              borderColor: 'primary.main',
            }
          : undefined,
        '&:focus-visible': clicavel
          ? { borderColor: 'primary.main', boxShadow: 4 }
          : undefined,
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 52,
              height: 52,
              flexShrink: 0,
              display: 'grid',
              placeItems: 'center',
              borderRadius: 2.5,
              color: '#fff',
              background: (t) => {
                const resolved =
                  typeof color === 'string' && color.includes('.')
                    ? color.split('.').reduce((acc, key) => acc?.[key], t.palette)
                    : color;
                const base = resolved || t.palette.primary.main;
                return `linear-gradient(145deg, ${base}, ${t.palette.secondary.main})`;
              },
              boxShadow: (t) => `0 8px 18px ${t.palette.primary.main}33`,
            }}
          >
            {icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Typography variant="h6" fontWeight={900} noWrap>
                {texto}
              </Typography>
              {delta && !oculto && (
                <Chip
                  size="small"
                  color="success"
                  icon={<TrendingUpIcon />}
                  label={delta}
                  sx={{ fontWeight: 700 }}
                />
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary" display="block">
                {subtitle}
              </Typography>
            )}
            {clicavel && (
              <Typography
                variant="caption"
                color="primary"
                sx={{ fontWeight: 700, mt: 0.5, display: 'block' }}
              >
                Ver detalhes →
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  if (!clicavel) return card;
  return (
    <Tooltip title={hint || 'Abrir detalhes'} arrow>
      <Box sx={{ height: '100%' }}>{card}</Box>
    </Tooltip>
  );
}

/// Barras coloridas (recharts) para vendas por dia.
function VendasPorDiaChart({ series, oculto }) {
  const theme = useTheme();
  if (oculto) {
    return <Typography color="text.secondary">Valores ocultos.</Typography>;
  }
  const data = series.map((dia, index) => ({
    label: diaCurto(dia.data),
    valor: Number(dia.valor) || 0,
    quantidade: Number(dia.quantidade) || 0,
    fill: CHART_COLORS[index % CHART_COLORS.length],
  }));

  return (
    <Box sx={{ width: '100%', height: 260 }}>
      <ResponsiveContainer>
        <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis
            tick={{ fontSize: 11 }}
            tickFormatter={(v) =>
              Number(v).toLocaleString('pt-BR', {
                notation: 'compact',
                maximumFractionDigits: 1,
              })
            }
          />
          <RechartsTooltip
            formatter={(value, _name, item) => [
              `${currency(value)} · ${item?.payload?.quantidade || 0} vendas`,
              'Faturamento',
            ]}
          />
          <Bar dataKey="valor" radius={[6, 6, 0, 0]}>
            {data.map((entry) => (
              <Cell key={entry.label} fill={entry.fill} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}

function FormasPagamentoChart({ series, oculto }) {
  const theme = useTheme();
  if (oculto) {
    return <Typography color="text.secondary">Valores ocultos.</Typography>;
  }
  const data = (series || []).map((forma, index) => ({
    name: forma.descricao,
    value: Number(forma.valor) || 0,
    quantidade: Number(forma.quantidade) || 0,
    fill: CHART_COLORS[index % CHART_COLORS.length],
  }));

  if (!data.length) {
    return <Typography color="text.secondary">Sem pagamentos no período.</Typography>;
  }

  return (
    <Box sx={{ width: '100%', height: 280 }}>
      <ResponsiveContainer>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            innerRadius={58}
            outerRadius={95}
            paddingAngle={2}
          >
            {data.map((entry) => (
              <Cell key={entry.name} fill={entry.fill} />
            ))}
          </Pie>
          <RechartsTooltip
            formatter={(value, name, item) => [
              `${currency(value)} · ${item?.payload?.quantidade || 0} lanç.`,
              name,
            ]}
          />
          <Legend
            verticalAlign="bottom"
            height={48}
            wrapperStyle={{ fontSize: 12, color: theme.palette.text.secondary }}
          />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );
}

function ProdutosMaisVendidosChart({ series, oculto }) {
  const theme = useTheme();
  if (oculto) {
    return <Typography color="text.secondary">Valores ocultos.</Typography>;
  }
  const data = (series || []).slice(0, 8).map((product, index) => ({
    name:
      String(product.descricao || '').length > 22
        ? `${String(product.descricao).slice(0, 22)}…`
        : product.descricao,
    fullName: product.descricao,
    quantidade: Number(product.quantidade) || 0,
    fill: CHART_COLORS[index % CHART_COLORS.length],
  }));

  if (!data.length) {
    return <Typography color="text.secondary">Sem itens vendidos no período.</Typography>;
  }

  return (
    <Box sx={{ width: '100%', height: 300 }}>
      <ResponsiveContainer>
        <BarChart
          layout="vertical"
          data={data}
          margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
          <XAxis type="number" tick={{ fontSize: 11 }} />
          <YAxis
            type="category"
            dataKey="name"
            width={120}
            tick={{ fontSize: 11 }}
          />
          <RechartsTooltip
            labelFormatter={(_label, payload) =>
              payload?.[0]?.payload?.fullName || _label
            }
            formatter={(value) => [
              Number(value).toLocaleString('pt-BR'),
              'Qtd',
            ]}
          />
          <Bar dataKey="quantidade" radius={[0, 6, 6, 0]}>
            {data.map((entry) => (
              <Cell key={entry.name} fill={entry.fill} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}

/// Rótulo "atualizado há X". Componente próprio para que o tique de 1s não
/// re-renderize os cartões e listas do painel.
function UltimaAtualizacao({ em }) {
  const [agora, setAgora] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setAgora(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  if (!em) return <>carregando...</>;

  const segundos = Math.floor((agora - em) / 1000);
  if (segundos < 5) return <>agora mesmo</>;
  if (segundos < 60) return <>há {segundos}s</>;
  return <>há {Math.floor(segundos / 60)} min</>;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const estabelecimentoId = useEstabelecimentoFiltroApi();
  const defaults = useMemo(defaultDates, []);
  const atalhos = useMemo(rangesRapidos, []);

  const [filters, setFilters] = useState(defaults);
  const [applied, setApplied] = useState(defaults);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [live, setLive] = useState(true);
  const [atualizadoEm, setAtualizadoEm] = useState(null);
  const [novidade, setNovidade] = useState(null);
  const [ocultarValores, setOcultarValores] = useState(
    () => localStorage.getItem('zen.dashboard.ocultarValores') === '1',
  );

  // Guarda o total anterior para destacar as vendas que entraram entre refreshes.
  const totalAnteriorRef = useRef(null);

  const load = useCallback(
    async ({ silencioso = false } = {}) => {
      if (silencioso) setRefreshing(true);
      else setLoading(true);
      try {
        const response = await getDashboard({ ...applied, estabelecimentoId });
        const novo = response.data;
        const anterior = totalAnteriorRef.current;

        if (anterior != null && novo.quantidadeVendas > anterior) {
          setNovidade(`+${novo.quantidadeVendas - anterior}`);
        }
        totalAnteriorRef.current = novo.quantidadeVendas;

        setData(novo);
        setAtualizadoEm(Date.now());
        setError('');
      } catch (e) {
        setError(e?.response?.data?.error || 'Não foi possível carregar o dashboard.');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [applied, estabelecimentoId]
  );

  // Troca de período reinicia a comparação, senão o filtro novo vira "novidade".
  useEffect(() => {
    totalAnteriorRef.current = null;
    setNovidade(null);
    load();
  }, [load]);

  useEffect(() => {
    if (!novidade) return undefined;
    const timer = setTimeout(() => setNovidade(null), HIGHLIGHT_MS);
    return () => clearTimeout(timer);
  }, [novidade]);

  // Modo ao vivo: pausa com a aba em segundo plano e recarrega ao voltar para ela.
  useEffect(() => {
    if (!live) return undefined;

    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') load({ silencioso: true });
    }, AUTO_REFRESH_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') load({ silencioso: true });
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [live, load]);

  const aplicarAtalho = (atalho) => {
    const periodo = { de: atalho.de, ate: atalho.ate };
    setFilters(periodo);
    setApplied(periodo);
  };

  const atalhoAtivo = (atalho) =>
    applied.de === atalho.de && applied.ate === atalho.ate;

  const periodoQuery = useMemo(() => {
    const p = new URLSearchParams();
    if (applied.de) p.set('de', applied.de);
    if (applied.ate) p.set('ate', applied.ate);
    return p;
  }, [applied]);

  const irPara = (path, extras = {}) => {
    const p = new URLSearchParams(periodoQuery);
    Object.entries(extras).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') p.delete(k);
      else p.set(k, String(v));
    });
    const qs = p.toString();
    navigate(qs ? `${path}?${qs}` : path);
  };

  const vendasPorDia = data?.vendasPorDia || [];
  const semPermissao = data?.valoresLiberados === false;
  const semEstoque = data?.estoqueLiberado === false;
  const mascarar = semPermissao || ocultarValores;
  const mascararEstoque = semEstoque || ocultarValores;

  const alternarValores = () => {
    setOcultarValores((atual) => {
      const proximo = !atual;
      localStorage.setItem('zen.dashboard.ocultarValores', proximo ? '1' : '0');
      return proximo;
    });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ md: 'flex-start' }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h4" fontWeight={900}>
              Dashboard
            </Typography>
            {data && !semPermissao && (
              <Tooltip title={ocultarValores ? 'Mostrar valores' : 'Ocultar valores'}>
                <IconButton
                  aria-label={ocultarValores ? 'Mostrar valores' : 'Ocultar valores'}
                  onClick={alternarValores}
                  size="small"
                >
                  {ocultarValores ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </Tooltip>
            )}
            {live && (
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: 'success.main',
                  '@keyframes pulsa': {
                    '0%': { opacity: 1, transform: 'scale(1)' },
                    '50%': { opacity: 0.35, transform: 'scale(1.35)' },
                    '100%': { opacity: 1, transform: 'scale(1)' },
                  },
                  animation: 'pulsa 1.8s ease-in-out infinite',
                }}
              />
            )}
          </Stack>
          <Typography color="text.secondary">
            Indicadores reais das vendas sincronizadas · atualizado{' '}
            <UltimaAtualizacao em={atualizadoEm} />
          </Typography>
        </Box>

        <Stack spacing={1} alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            <TextField
              label="De"
              type="date"
              size="small"
              value={filters.de}
              onChange={(e) => setFilters((p) => ({ ...p, de: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Até"
              type="date"
              size="small"
              value={filters.ate}
              onChange={(e) => setFilters((p) => ({ ...p, ate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <Button variant="contained" onClick={() => setApplied({ ...filters })}>
              Aplicar
            </Button>
            <Tooltip title="Atualizar agora">
              <IconButton onClick={() => load({ silencioso: true })} disabled={refreshing}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            {atalhos.map((atalho) => (
              <Chip
                key={atalho.label}
                label={atalho.label}
                size="small"
                color={atalhoAtivo(atalho) ? 'primary' : 'default'}
                variant={atalhoAtivo(atalho) ? 'filled' : 'outlined'}
                onClick={() => aplicarAtalho(atalho)}
              />
            ))}
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={live}
                  onChange={(e) => setLive(e.target.checked)}
                />
              }
              label={<Typography variant="body2">Ao vivo</Typography>}
              sx={{ ml: 0.5, mr: 0 }}
            />
          </Stack>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {semPermissao && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Seu perfil não tem permissão de relatórios. Faturamento, vendas e clientes das vendas não são carregados.
        </Alert>
      )}

      {/* Barra fina no topo: o refresh automático não deve piscar a tela toda. */}
      <Box sx={{ height: 4, mb: 2 }}>
        {(loading || refreshing) && <LinearProgress sx={{ borderRadius: 2 }} />}
      </Box>

      {loading && !data ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} lg={3}>
              <KpiCard
                title="Faturamento"
                value={data?.faturamento}
                format={currency}
                oculto={mascarar}
                subtitle={mascarar ? undefined : `${data?.quantidadeVendas || 0} vendas no período`}
                icon={<AttachMoneyIcon />}
                hint="Abrir relatório de vendas"
                onClick={semPermissao ? undefined : () => irPara('/reports', { tab: 0 })}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <KpiCard
                title="Vendas"
                value={data?.quantidadeVendas}
                delta={mascarar ? null : novidade}
                oculto={mascarar}
                subtitle={mascarar ? undefined : `${data?.vendasCanceladas || 0} canceladas`}
                icon={<ShoppingCartIcon />}
                color="info.main"
                hint="Abrir relatório de vendas"
                onClick={semPermissao ? undefined : () => irPara('/reports', { tab: 0 })}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <KpiCard
                title="Ticket médio"
                value={data?.ticketMedio}
                format={currency}
                oculto={mascarar}
                icon={<ReceiptLongIcon />}
                color="secondary.main"
                hint="Abrir relatório de vendas"
                onClick={semPermissao ? undefined : () => irPara('/reports', { tab: 0 })}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <KpiCard
                title="Sessões de caixa abertas"
                value={data?.sessoesAbertas}
                oculto={ocultarValores}
                icon={<PointOfSaleIcon />}
                color="success.main"
                hint="Ver sessões abertas"
                onClick={() => irPara('/cash-sessions', { status: 'aberta' })}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <KpiCard
                title="Recebimentos de convênio"
                value={data?.recebimentosConvenio}
                format={currency}
                oculto={mascarar}
                icon={<WalletIcon />}
                color="warning.main"
                hint="Abrir relatório de convênio"
                onClick={semPermissao ? undefined : () => irPara('/reports', { tab: 3 })}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <KpiCard
                title="Clientes ativos"
                value={data?.clientesAtivos}
                oculto={ocultarValores}
                icon={<ShoppingCartIcon />}
                color="info.main"
                hint="Abrir cadastro de clientes"
                onClick={() => navigate('/clients')}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <KpiCard
                title="Produtos em estoque baixo"
                value={data?.produtosEstoqueBaixo}
                oculto={mascararEstoque}
                icon={<WarningIcon />}
                color="error.main"
                hint="Abrir relatório de estoque baixo"
                onClick={semEstoque ? undefined : () => navigate('/reports/estoque-situacao?tipo=baixo')}
              />
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} lg={7}>
              <Paper
                onClick={semPermissao ? undefined : () => irPara('/reports', { tab: 0 })}
                sx={{
                  p: 2,
                  height: '100%',
                  cursor: semPermissao ? 'default' : 'pointer',
                  transition: 'box-shadow .2s, border-color .2s',
                  border: '1px solid transparent',
                  '&:hover': semPermissao ? undefined : { boxShadow: 4, borderColor: 'primary.main' },
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="h6" fontWeight={800}>
                    Vendas por dia
                  </Typography>
                  {!semPermissao && (
                    <Typography variant="caption" color="primary" fontWeight={700}>
                      Ver relatório →
                    </Typography>
                  )}
                </Stack>
                {mascarar ? (
                  <Typography color="text.secondary">Valores ocultos.</Typography>
                ) : vendasPorDia.length === 0 ? (
                  <Typography color="text.secondary">Sem vendas no período.</Typography>
                ) : (
                  <Box onClick={(e) => e.stopPropagation()}>
                    <VendasPorDiaChart series={vendasPorDia} />
                  </Box>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} lg={5}>
              <Paper
                onClick={semPermissao ? undefined : () => irPara('/reports', { tab: 1 })}
                sx={{
                  p: 2,
                  height: '100%',
                  cursor: semPermissao ? 'default' : 'pointer',
                  transition: 'box-shadow .2s, border-color .2s',
                  border: '1px solid transparent',
                  '&:hover': semPermissao ? undefined : { boxShadow: 4, borderColor: 'primary.main' },
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="h6" fontWeight={800}>
                    Formas de pagamento
                  </Typography>
                  {!semPermissao && (
                    <Typography variant="caption" color="primary" fontWeight={700}>
                      Ver relatório →
                    </Typography>
                  )}
                </Stack>
                <Box onClick={(e) => e.stopPropagation()}>
                  <FormasPagamentoChart series={data?.formasPagamento || []} oculto={mascarar} />
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                onClick={semPermissao ? undefined : () => irPara('/reports', { tab: 2 })}
                sx={{
                  p: 2,
                  height: '100%',
                  cursor: semPermissao ? 'default' : 'pointer',
                  transition: 'box-shadow .2s, border-color .2s',
                  border: '1px solid transparent',
                  '&:hover': semPermissao ? undefined : { boxShadow: 4, borderColor: 'primary.main' },
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="h6" fontWeight={800}>
                    Produtos mais vendidos
                  </Typography>
                  {!semPermissao && (
                    <Typography variant="caption" color="primary" fontWeight={700}>
                      Ver relatório →
                    </Typography>
                  )}
                </Stack>
                <Box onClick={(e) => e.stopPropagation()}>
                  <ProdutosMaisVendidosChart series={data?.produtosMaisVendidos || []} oculto={mascarar} />
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                onClick={semPermissao ? undefined : () => irPara('/reports', { tab: 0 })}
                sx={{
                  p: 2,
                  height: '100%',
                  cursor: semPermissao ? 'default' : 'pointer',
                  transition: 'box-shadow .2s, border-color .2s',
                  border: '1px solid transparent',
                  '&:hover': semPermissao ? undefined : { boxShadow: 4, borderColor: 'primary.main' },
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="h6" fontWeight={800}>
                    Vendas recentes
                  </Typography>
                  {!semPermissao && (
                    <Typography variant="caption" color="primary" fontWeight={700}>
                      Ver relatório →
                    </Typography>
                  )}
                </Stack>
                {mascarar ? (
                  <Typography color="text.secondary">Valores ocultos.</Typography>
                ) : (
                <List disablePadding>
                  {(data?.vendasRecentes || []).map((sale, index) => (
                    <Box key={sale.id}>
                      <ListItem disableGutters>
                        <ListItemText
                          primary={`${sale.cliente} · ${sale.pdv}`}
                          secondary={new Date(sale.data).toLocaleString('pt-BR')}
                        />
                        <Stack alignItems="flex-end">
                          <Typography fontWeight={800}>{currency(sale.valor)}</Typography>
                          {sale.cancelada && (
                            <Chip size="small" color="error" label="Cancelada" />
                          )}
                        </Stack>
                      </ListItem>
                      {index < data.vendasRecentes.length - 1 && <Divider />}
                    </Box>
                  ))}
                  {!data?.vendasRecentes?.length && (
                    <Typography color="text.secondary">Nenhuma venda no período.</Typography>
                  )}
                </List>
                )}
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

      <Stack direction="row" spacing={1} sx={{ mt: 3 }} flexWrap="wrap" useFlexGap>
        {!semPermissao && (
          <Button variant="outlined" onClick={() => irPara('/reports', { tab: 0 })}>
            Ver relatórios
          </Button>
        )}
        <Button variant="outlined" onClick={() => irPara('/cash-sessions')}>
          Sessões de caixa
        </Button>
        <Button variant="outlined" onClick={() => navigate('/stock-movement')}>
          Movimentar estoque
        </Button>
      </Stack>
    </Box>
  );
}
